/**
 * CodeLintService - lint sources for the CodeMirror step-code editor.
 *
 * Provides two CM6 lint sources:
 *  - jsLintSource: synchronous ESLint (eslint-linter-browserify) with an
 *    errors-only rule set. no-undef is seeded with the JS runtime globals and
 *    the API namespaces so host modules like `ehq` aren't flagged.
 *  - pythonLintSource: async pyflakes via the shared Pyodide runtime
 *    (PythonService), mapped to CM diagnostics.
 *
 * The wrapper component wires these into a lint compartment per editor mode.
 * (EHQ-108)
 */

import { Injectable, inject } from '@angular/core';
import { Diagnostic } from '@codemirror/lint';
import { EditorView } from '@codemirror/view';
import { esLint } from '@codemirror/lang-javascript';

import { ApiReferenceService } from '../api-reference/api-reference.service';
import { PythonService } from '../python/python.service';
import {
  ARROW_FLIGHT, ARROW_FLIGHT_COMPAT, ARROW_FLIGHT_SHORT, BOX, BOX_UPPER, CONVERT,
  EHQ, EHQ_UPPER,
} from '../../constants/additional-methods.constants';

// eslint-linter-browserify is ~350KB gzipped, so it's loaded lazily via a
// dynamic import — it lands in its own chunk fetched only when a JS editor
// first lints, keeping it out of the initial app bundle. (EHQ-108)

/**
 * Errors-only rule set: real problems, no style nagging (matches the pyflakes
 * philosophy on the Python side). 'error' → CM red, 'warn' → CM yellow.
 */
type RuleSetting = 'error' | 'warn' | ['error' | 'warn', Record<string, unknown>];

const JS_RULES: Record<string, RuleSetting> = {
  'no-undef': 'error',
  // Unused local vars are flagged, but not unused `catch (e)` bindings — the
  // empty-catch pattern is common and harmless, so caughtErrors: 'none'. (EHQ-108)
  'no-unused-vars': ['warn', { caughtErrors: 'none' }],
  'no-unreachable': 'error',
  'no-const-assign': 'error',
  'no-func-assign': 'error',
  'no-class-assign': 'error',
  'no-import-assign': 'error',
  'no-dupe-keys': 'error',
  'no-dupe-args': 'error',
  'no-dupe-class-members': 'error',
  'no-dupe-else-if': 'error',
  'no-duplicate-case': 'error',
  'no-cond-assign': 'error',
  'no-obj-calls': 'error',
  'no-sparse-arrays': 'error',
  'no-unexpected-multiline': 'error',
  'no-unsafe-negation': 'error',
  'no-unsafe-finally': 'error',
  'no-self-assign': 'error',
  'no-compare-neg-zero': 'error',
  'no-constant-binary-expression': 'error',
  'use-isnan': 'error',
  'valid-typeof': 'error',
  'getter-return': 'error',
  'no-setter-return': 'error',
  'no-async-promise-executor': 'error',
};

/**
 * JS runtime globals available to step code. Curated on purpose — pulling the
 * full `globals` package would bloat the browser bundle. Language builtins
 * plus the host functions the sandbox exposes; API namespaces are added
 * dynamically from ApiReferenceService.
 */
const JS_GLOBALS: string[] = [
  // ── ES language builtins ──
  'globalThis', 'Object', 'Function', 'Boolean', 'Symbol', 'Error', 'EvalError',
  'RangeError', 'ReferenceError', 'SyntaxError', 'TypeError', 'URIError',
  'AggregateError', 'Number', 'BigInt', 'Math', 'Date', 'String', 'RegExp',
  'Array', 'Int8Array', 'Uint8Array', 'Uint8ClampedArray', 'Int16Array',
  'Uint16Array', 'Int32Array', 'Uint32Array', 'Float32Array', 'Float64Array',
  'BigInt64Array', 'BigUint64Array', 'Map', 'Set', 'WeakMap', 'WeakSet',
  'ArrayBuffer', 'SharedArrayBuffer', 'DataView', 'JSON', 'Promise', 'Reflect',
  'Proxy', 'Intl', 'WebAssembly', 'NaN', 'Infinity', 'undefined', 'parseInt',
  'parseFloat', 'isNaN', 'isFinite', 'decodeURI', 'decodeURIComponent',
  'encodeURI', 'encodeURIComponent', 'eval', 'structuredClone', 'queueMicrotask',
  // ── Common host/runtime globals ──
  'console', 'setTimeout', 'clearTimeout', 'setInterval', 'clearInterval',
  'fetch', 'URL', 'URLSearchParams', 'TextEncoder', 'TextDecoder', 'Blob',
  'FormData', 'AbortController', 'AbortSignal', 'atob', 'btoa', 'crypto',
  'performance',
  // ── Enlighten step-JS runtime globals (javascript.helper.ts) ──
  'data', 'modules', 'print', 'getJobArguments',
];

/**
 * Built-in API namespaces injected into every step-JS VM (javascript.service.ts
 * registerCustomMethods + uppercase/compat aliases). Sourced from the runtime
 * constants so the lint globals never drift from what actually gets injected —
 * and, unlike the API-reference cache, they're available synchronously so
 * `ehq`/`box`/etc. are never briefly flagged as undefined. (EHQ-108)
 */
const NAMESPACE_GLOBALS: readonly string[] = [
  EHQ, EHQ_UPPER, BOX, BOX_UPPER, CONVERT,
  ARROW_FLIGHT, ARROW_FLIGHT_COMPAT, ARROW_FLIGHT_SHORT,
];

@Injectable({ providedIn: 'root' })
export class CodeLintService {
  private readonly apiReferenceService = inject(ApiReferenceService);
  private readonly pythonService = inject(PythonService);

  private linterModule?: Promise<typeof import('eslint-linter-browserify')>;
  private eslint?: InstanceType<typeof import('eslint-linter-browserify').Linter>;
  private jsLinterFn?: (view: EditorView) => Diagnostic[];
  private jsGlobalsSignature = '';

  /**
   * CM lint source for JavaScript step code. Async because the ESLint engine
   * is lazy-loaded on first use; CM's linter() accepts a Promise source.
   */
  readonly jsLintSource = async (view: EditorView): Promise<Diagnostic[]> => {
    try {
      const lint = await this.getJsLinter();
      return lint(view);
    } catch (error) {
      console.warn('CodeLintService: JS lint failed', error);
      return [];
    }
  };

  /** CM lint source for Python step code (async — runs pyflakes in Pyodide). */
  readonly pythonLintSource = async (view: EditorView): Promise<Diagnostic[]> => {
    const doc = view.state.doc;
    const diags = await this.pythonService.lintCode(doc.toString());
    return diags.map((d) => {
      const lineNo = Math.min(Math.max(d.line, 1), doc.lines);
      const line = doc.line(lineNo);
      const from = Math.min(line.from + Math.max(d.col - 1, 0), line.to);
      return {
        from,
        to: line.to > from ? line.to : from,
        severity: d.severity,
        message: d.message,
        source: 'pyflakes',
      };
    });
  };

  /**
   * Lazily load the ESLint engine, then (re)build the lint source, refreshing
   * seeded globals when the set of API namespaces changes (the reference cache
   * loads asynchronously).
   */
  private async getJsLinter(): Promise<(view: EditorView) => Diagnostic[]> {
    if (!this.eslint) {
      this.linterModule ??= import('eslint-linter-browserify');
      try {
        const { Linter } = await this.linterModule;
        this.eslint = new Linter();
      } catch (error) {
        // ??= won't replace a defined-but-rejected promise, so a single failed
        // chunk fetch would otherwise stay memoized and silently kill JS linting
        // for the whole session. Reset so a later pass can retry. (EHQ-108)
        this.linterModule = undefined;
        this.eslint = undefined;
        throw error;
      }
    }
    const namespaces = this.apiReferenceService.getNamespaceIdentifiers();
    const signature = namespaces.slice().sort().join(',');
    if (!this.jsLinterFn || signature !== this.jsGlobalsSignature) {
      const globals: Record<string, 'readonly'> = {};
      for (const name of [...JS_GLOBALS, ...NAMESPACE_GLOBALS]) {
        globals[name] = 'readonly';
      }
      // Supplement with dynamic namespaces (WASM/project modules) once the
      // API-reference cache has loaded; the built-ins above cover the rest.
      for (const name of namespaces) {
        if (name) {
          globals[name] = 'readonly';
        }
      }
      this.jsLinterFn = esLint(this.eslint, {
        languageOptions: {
          ecmaVersion: 'latest',
          // 'module' (not 'script') so top-level await parses — step JS
          // supports it and uses it (e.g. `const x = await ehq.getFile(...)`).
          sourceType: 'module',
          globals,
        },
        rules: JS_RULES,
      });
      this.jsGlobalsSignature = signature;
    }
    return this.jsLinterFn;
  }
}
